import { useEffect, useRef, useMemo, useState, useCallback } from "react"
import { pick, omitBy, isNil, isEqual, debounce, pickBy } from "lodash"
import { useSaveContextPreferences, useReadContextPreference } from "hooks/preferences"
import { OK, ERROR } from "helpers/data-statuses"

/**
 * Single hook that handles both loading and saving preferences
 * Replaces the need for separate PreferenceSaver and PreferenceLoader components
 *
 * @param {string} subType - The preference subtype (e.g., "dashboard", "asset-view")
 * @param {string[]} preferenceFields - Array of field names to save as preferences
 * @param {object} state - Current state to save preferences from
 * @param {function} dispatch - Dispatch function to update state with loaded preferences
 * @param {function} setPreferencesAction - Action creator for setting preferences (e.g., setPreferences)
 * @param {number} debounceTime - Debounce time for saving (default 300ms)
 * @returns {boolean} isPreferencesLoaded - Whether preferences have been loaded
 */
export function usePreferences(
  subType,
  preferenceFields,
  state,
  dispatch,
  setPreferencesAction,
  debounceTime = 300,
) {
  // Hooks for saving and loading
  const savePreferences = useSaveContextPreferences(subType)
  const [preferences, preferenceStatus] = useReadContextPreference(subType)
  const [isPreferencesLoaded, setIsPreferencesLoaded] = useState(false)

  // ===== LOADING LOGIC (runs once on mount) =====
  const isInitialLoadRef = useRef(false)

  const handlePreferencesLoaded = useCallback((preferences) => {
    // Only dispatch if we have preferences to load
    if (Object.keys(preferences).length > 0) {
      dispatch(setPreferencesAction(preferences))
    }

    setIsPreferencesLoaded(true)
  }, [dispatch, setPreferencesAction])

  useEffect(() => {
    // Avoid multiple loads
    if (isInitialLoadRef.current) {
      return
    }

    if (preferenceStatus === OK) {
      isInitialLoadRef.current = true

      // Clean up preferences and ensure proper types
      // Only filter out null/undefined, preserve false boolean values
      const cleanedPreferences = preferences ? pickBy(preferences, (value) => !isNil(value)) : {}
      handlePreferencesLoaded(cleanedPreferences)
    } else if (preferenceStatus === ERROR) {
      isInitialLoadRef.current = true
      handlePreferencesLoaded({})
    }
  }, [preferences, preferenceStatus, handlePreferencesLoaded])

  // ===== SAVING LOGIC (runs on state changes) =====
  const previousPreferencesRef = useRef(null)
  const isInitialSaveRef = useRef(true)

  // Create a debounced save function to avoid rapid successive saves
  const debouncedSave = useMemo(
    () => debounce((preferencesToSave) => {
      savePreferences(preferencesToSave)
    }, debounceTime),
    [savePreferences, debounceTime],
  )

  useEffect(() => {
    if (!isPreferencesLoaded || !state) {
      return
    }

    // Extract only preference-related fields and remove null/undefined values
    const currentPreferences = omitBy(pick(state, preferenceFields), isNil)

    // Skip if this is the initial load
    if (isInitialSaveRef.current) {
      previousPreferencesRef.current = currentPreferences
      isInitialSaveRef.current = false
      return
    }

    // Only save if preferences have actually changed
    if (!isEqual(previousPreferencesRef.current, currentPreferences)) {
      previousPreferencesRef.current = currentPreferences

      // Only save if we have at least one preference
      if (Object.keys(currentPreferences).length > 0) {
        debouncedSave(currentPreferences)
      }
    }
  }, [state, isPreferencesLoaded, debouncedSave, preferenceFields])

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSave.cancel()
    }
  }, [debouncedSave])

  return isPreferencesLoaded
}
